import React, { useState } from 'react';
import { Store, ChevronDown, ChevronRight } from 'lucide-react';
import StoreDayBreakdown from './StoreDayBreakdown';

/** Pesos sin decimales: en estas cifras los centavos son ruido. */
const pesos = (n) => `$${Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })}`;

const StoreRedemptionSummary = ({ stores, grandTotal, onStoreClick, onDayClick }) => {
  const [expanded, setExpanded] = useState(new Set());

  /* Totales del rango, para no tener que sumar las tiendas a ojo. */
  const totales = stores.reduce(
    (acc, s) => ({ spend: acc.spend + (s.spend || 0), saved: acc.saved + (s.saved || 0) }),
    { spend: 0, saved: 0 }
  );

  const toggleExpand = (storeId, e) => {
    e.stopPropagation();
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(storeId)) next.delete(storeId);
      else next.add(storeId);
      return next;
    });
  };

  return (
    <>
      {/* Suma de las columnas nuevas, para no hacerlo a ojo por tienda. */}
      <div className="mt-4 flex items-center justify-between py-3 px-4 bg-black/5 rounded-xl mb-4 border border-black/8">
        <span className="text-gray-600 font-medium">Total general (rango)</span>
        <div className="flex items-baseline gap-5">
          <span className="text-sm text-gray-500">
            consumo <strong className="text-gray-900">{pesos(totales.spend)}</strong>
          </span>
          <span className="text-sm text-gray-500">
            ahorro <strong style={{ color: '#F73E48' }}>{pesos(totales.saved)}</strong>
          </span>
          <span className="text-2xl font-bold text-gray-900">{grandTotal}</span>
        </div>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-gray-200">
        <table className="w-full min-w-[400px]">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="w-8 px-2 py-3" />
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Tienda</th>
              <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Redenciones</th>
              {/* Módulo 7: el dinero que mueve cada tienda. «Consumo» es lo que
                  gastaron los clientes ahí; «ahorro» lo que se les descontó. */}
              <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Consumo</th>
              <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Ahorro</th>
              <th className="px-4 py-3 w-20" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {stores.map((row) => {
              const hasDays = (row.byDay?.length ?? 0) > 0;
              const isExpanded = expanded.has(row.storeId);
              return (
                <React.Fragment key={row.storeId}>
                  <tr
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => onStoreClick(row.storeId, row.storeName)}
                  >
                    <td className="px-2 py-3">
                      {hasDays ? (
                        <button
                          type="button"
                          onClick={(e) => toggleExpand(row.storeId, e)}
                          className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                      ) : (
                        <span className="w-4 inline-block" />
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Store className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-900 font-medium">{row.storeName}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right text-gray-900 font-semibold">{row.total}</td>
                    <td className="px-6 py-4 text-right">
                      <span className="text-gray-900 font-semibold">{pesos(row.spend)}</span>
                      {/* Solo se avisa cuando parte de los canjes no trae monto:
                          si no, la cifra parece más baja de lo que fue. */}
                      {row.withAmount !== undefined && row.withAmount < row.total && (
                        <div className="text-[11px] text-gray-400">
                          de {row.withAmount} de {row.total} canjes
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right font-semibold" style={{ color: '#F73E48' }}>
                      {pesos(row.saved)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-400 hover:text-gray-600">Ver detalle</span>
                    </td>
                  </tr>
                  {isExpanded && hasDays && (
                    <StoreDayBreakdown
                      byDay={row.byDay}
                      storeId={row.storeId}
                      storeName={row.storeName}
                      onDayClick={onDayClick}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default StoreRedemptionSummary;
